import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LoaderComponent } from '../../../../components/widgets/loader/loader.component';
import { LoaderConfig } from '../../../../components/widgets/loader/loader.config';
import { MeetingMinuteFormComponent } from '../../components/meeting-minute-form/meeting-minute-form.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';

import { MeetingMinute } from '../../../../models/entities/meeting-minute';

@Component({
  selector: 'app-meeting-minutes-edit-page',
  imports: [LoaderComponent, MeetingMinuteFormComponent],
  templateUrl: './meeting-minutes-edit-page.component.html',
  styleUrl: './meeting-minutes-edit-page.component.scss',
})
export class MeetingMinutesEditPageComponent implements OnInit, OnDestroy {

  meetingMinuteId?: number;
  meetingMinute?: MeetingMinute;
  isEditMode: boolean = false;

  isLoading: boolean = false;
  loaderConfig: LoaderConfig = {
    type: 'spinner',
    text: 'Saving...',
    overlay: true
  };

  readonly LOADER_CONTEXT = 'meeting-minutes-edit-page';
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService
  ) { }

  ngOnInit(): void {
    window.scrollTo({ top: 0 });

    this.loaderService.getLoadingAsObservable(this.LOADER_CONTEXT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => this.isLoading = isLoading);

    const paramId = this.route.snapshot.paramMap.get('meetingMinuteId');
    if (paramId) {
      this.meetingMinuteId = Number(paramId);
      this.isEditMode = true;
      this.loadMeetingMinute(this.meetingMinuteId);
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.LOADER_CONTEXT);
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Update Meeting Minute' : 'Create Meeting Minute';
  }

  onFormSavedData(meetingMinute: MeetingMinute): void {
    const isFirstSave = !this.isEditMode;

    this.meetingMinute = meetingMinute;
    this.meetingMinuteId = meetingMinute.meetingMinuteId ?? this.meetingMinuteId;

    if (isFirstSave && this.meetingMinuteId) {
      this.routeService.meetingMinutesDetail(Number(this.meetingMinuteId));
    }
  }

  private loadMeetingMinute(meetingMinuteId: number): void {
    this.loaderService.startLoading(this.LOADER_CONTEXT);

    this.qualityManagementService.detailMeetingMinute(meetingMinuteId).subscribe({
      next: (response: any) => {
        const meetingMinute: MeetingMinute | null =
          this.qualityManagementService.readResponseKey(response, 'meetingMinute') ?? null;
        this.loaderService.stopLoading(this.LOADER_CONTEXT);

        if (!meetingMinute) {
          this.alertNotificationService.error('Meeting minute not found.');
          return;
        }

        this.meetingMinute = meetingMinute;
      },
      error: (error: any) => {
        console.error('Error loading the meeting minute:', error);
        this.loaderService.stopLoading(this.LOADER_CONTEXT);
        this.alertNotificationService.error(
          this.qualityManagementService.meetingMinuteErrorMessage(error, 'Failed to load the meeting minute.')
        );
      }
    });
  }
}
